import React, { useState, useEffect, useContext } from "react";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";
import { FOREIGN_INCOME_TRACKER_SCHEDULES } from "../../../utils/data/controlData";
import ForeignIncomeTrackerSchedule from "../../../components/Schedules/ForeignIncomeTracker/ForeignIncomeTrackerSchedule";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { AuthContext } from "../../../contexts/AuthContext";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import { initSchedule } from "../../../utils/data/initData";
import { handleScheduleDeletion } from "../utils/handleScheduleDeletion";

const ForeignIncomeTracker = ({ company, scheduleId }) => {
  const { updatePLSchedule, getScheduleData, deleteScheduleByScheduleType } =
    useContext(AuthContext);
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState({});

  const { canEdit: canEditForeignIncomeTrackerSchedule } = useAccessControl(
    FOREIGN_INCOME_TRACKER_SCHEDULES
  );

  const [loading, setLoading] = useState(true);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (!company || !scheduleId) {
        handleError(
          "We encountered an issue while fetching the data. Please refresh the page or try again shortly."
        );
        return;
      }

      try {
        const response = await getScheduleData(
          scheduleId,
          "Unremitted_Foreign_Income"
        );
        const scheduleData = response.schedule;

        if (scheduleData) {
          setSchedule(
            initSchedule(scheduleData, "Unremitted Foreign Income Tracking")
          );
          setScheduleDetails({
            dataRows: {
              rows: scheduleData.rows || [],
              trackingScheduleData: scheduleData.trackingScheduleData || [],
              ftcClaims: scheduleData.ftcClaims || {},
              previousYearData: scheduleData.previousYearData || [],
            },
            foreignTaxCreditScheduleRef:
              scheduleData.foreignTaxCreditScheduleRef || "",
          });
        }
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [saveTrigger, scheduleId, getScheduleData, company.uen, updatePLSchedule]);

  const saveSchedule = async (schedule, scheduleDetails) => {
    if (!company || !scheduleId) {
      handleError("Unexpected error happens. Please try again later.");
      return;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
      scheduleType: "Unremitted_Foreign_Income",
      scheduleTitle: schedule.name,
      taxAdjustment: schedule.taxAdjustment,
      rows: scheduleDetails.dataRows,
      previousYearData: scheduleDetails.previousYearData,
      trackingScheduleData: scheduleDetails.trackingScheduleData,
      calculatedTrackingData: scheduleDetails.calculatedTrackingData,
      ftcClaims: scheduleDetails.ftcClaims,
      notes: schedule.notes,
      reviewNotes: schedule.reviewNotes,
      totalAdjustment: scheduleDetails.totalAdjustment,
      doc: scheduleDetails.doc,
    };

    setLoading(true);
    try {
      const response = await updatePLSchedule(updatedSchedule);
      setLoading(false);

      if (response) {
        handleSuccess("The schedule has been saved successfully.");
        setSaveTrigger((prev) => !prev);
      } else {
        handleError(
          "There was a problem saving the schedule. Please contact support if the issue persists."
        );
      }
    } catch (error) {
      setLoading(false);
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists."
      );
    }
  };

  const deleteSchedule = async () => {
    await handleScheduleDeletion({
      scheduleId,
      deleteFn: deleteScheduleByScheduleType,
      handleSuccess,
      handleError,
      setLoading,
    });
  };

  return (
    <>
      {loading ? (
        <ComponentLoading />
      ) : (
        <ForeignIncomeTrackerSchedule
          company={company}
          schedule={schedule}
          scheduleDetails={scheduleDetails}
          saveSchedule={saveSchedule}
          deleteSchedule={deleteSchedule}
          canEditSchedule={canEditForeignIncomeTrackerSchedule}
        />
      )}
    </>
  );
};

export default ForeignIncomeTracker;
